import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Button, Paper, Grid, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, useTheme,
  CircularProgress, IconButton, Tooltip, useMediaQuery
} from '@mui/material';
import { ArrowBack, Download, Refresh } from '@mui/icons-material';
import { useGetEmpDataAdminQuery } from 'state/api';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';

const RmDetails = () => {
  const { RmMgName } = useSelector((state) => state.auth);
  
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: response, isLoading, isError, refetch } = useGetEmpDataAdminQuery({
    RmMgName: RmMgName || ''
  });

  // Extract actual data array from the response
  const data = response?.data || [];
  const success = response?.success || false;

  console.log("API response", response);
  console.log("Actual data array", data);

  // Extract dynamic date columns
  const getDateColumns = () => {
    if (data.length === 0) return [];
    
    const dateColumns = [];
    for (const key in data[0]) {
      if (key.includes('-') && key.includes('(') && key.includes(')')) {
        dateColumns.push(key);
      }
    }
    
    // Sort columns by date
    return dateColumns.sort((a, b) => {
      const dateA = new Date(a.split('(')[0].split('-').reverse().join('-'));
      const dateB = new Date(b.split('(')[0].split('-').reverse().join('-'));
      return dateA - dateB;
    });
  };

  const dateColumns = getDateColumns();

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Doctor Approvals");
    XLSX.writeFile(workbook, `${RmMgName || 'Export'}_approvals.xlsx`);
  };

  if (isLoading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: theme.palette.background.default
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (isError || !success) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 3,
        textAlign: 'center',
        padding: 3,
        background: theme.palette.background.default
      }}>
        <Typography variant="h5" color="error">
          {isError ? "Error loading data" : "No data available"}
        </Typography>
        <Button 
          variant="outlined"
          startIcon={<Refresh />}
          onClick={refetch}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      padding: { xs: 2, md: 4 },
      maxWidth: '1800px',
      margin: '0 auto',
      minHeight: '100vh',
      background: theme.palette.background.default
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 4,
        flexWrap: 'wrap'
      }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: theme.palette.text.primary }}>
          <ArrowBack />
        </IconButton>

        <Typography variant="h4" sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          flexGrow: 1,
          fontSize: { xs: '1.5rem', md: '2rem' }
        }}>
          {RmMgName || 'Medical Representative'}
        </Typography>

        <Tooltip title="Export to Excel">
          <Button
            variant="contained"
            onClick={handleExport}
            startIcon={<Download />}
            sx={{
              py: 1,
              px: 3,
              borderRadius: 3,
              textTransform: 'none',
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            Export
          </Button>
        </Tooltip>

        <IconButton
          onClick={handleExport}
          sx={{
            color: theme.palette.primary.main,
            display: { xs: 'flex', sm: 'none' }
          }}
        >
          <Download />
        </IconButton>
      </Box>

      {/* Data Table */}
      <Paper sx={{
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: theme.shadows[2],
        background: theme.palette.background.paper,
        overflowX: 'auto'
      }}>
        <TableContainer sx={{ maxHeight: '600px' }}>
          <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerStyle(theme, isMobile)}>
                  {isMobile ? 'Employee' : 'Employee Name'}
                </TableCell>
                <TableCell sx={headerStyle(theme, isMobile)}>
                  {isMobile ? 'Doctor' : 'Doctor Name'}
                </TableCell>
                
                {dateColumns.map((column, index) => (
                  <TableCell 
                    key={index} 
                    sx={headerStyle(theme, isMobile)} 
                    align="center"
                  >
                    {column.split('_')[0]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:nth-of-type(odd)': {
                      background: theme.palette.action.hover
                    },
                    '&:hover': {
                      background: theme.palette.action.selected
                    }
                  }}
                >
                  <TableCell>
                    <Typography fontWeight={600}>
                      {row.MrName || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="textSecondary">
                      {!row.DoctName || row.DoctName === '\\N' ? 'N/A' : row.DoctName}
                    </Typography>
                  </TableCell>
                  
                  {dateColumns.map((column, colIndex) => (
                    <TableCell key={colIndex} align="center">
                      {row[column] || '0'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Empty State */}
      {data.length === 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          textAlign: 'center',
          mt: 4,
          flexDirection: 'column',
          gap: 2
        }}>
          <img 
            src="/empty-state.svg" 
            alt="No data" 
            style={{ width: isMobile ? '120px' : '180px', opacity: 0.8 }}
          />
          <Typography variant="h6" color="textSecondary">
            No approval records found
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const headerStyle = (theme, isMobile) => ({
  background: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  fontSize: isMobile ? '0.8rem' : '0.95rem',
  borderBottom: `2px solid ${theme.palette.primary.dark}`,
  whiteSpace: 'nowrap'
});

export default RmDetails;